import { useState, useEffect, useRef, useCallback } from 'react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Play, Square, Clock, Loader2, AlertTriangle, Layers } from 'lucide-react';
import { Badge } from '../ui/badge';

const pad = (n) => String(n).padStart(2, '0');
const localNow = () => {
  const d = new Date();
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

export const MobileClockWidget = () => {
  const { api } = useMobileAuth();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [breakDialog, setBreakDialog] = useState(false);
  const [tookBreak, setTookBreak] = useState('no');
  const [breakMinutes, setBreakMinutes] = useState('30');
  const [overwriteDialog, setOverwriteDialog] = useState(false);
  const [blocksPreview, setBlocksPreview] = useState(null);
  const pendingOff = useRef(null);
  const tickRef = useRef(null);

  const fetchActive = useCallback(async () => {
    try {
      const res = await api.get('/api/mobile/timesheets/active');
      setShift(res.data || null);
    } catch {
      setShift(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchActive(); }, [fetchActive]);

  // Running timer
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (!shift?.clock_on_at) { setElapsed('00:00:00'); return; }
    const started = new Date(shift.clock_on_at).getTime();
    const update = () => {
      const diff = Math.max(0, Math.floor((Date.now() - started) / 1000));
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(`${pad(h)}:${pad(m)}:${pad(s)}`);
    };
    update();
    tickRef.current = setInterval(update, 1000);
    return () => clearInterval(tickRef.current);
  }, [shift]);

  const handleClockOn = async () => {
    setBusy(true);
    try {
      const { date, time } = localNow();
      const res = await api.post('/api/mobile/timesheets/clock-on', { date, start_time: time });
      setShift(res.data);
      toast.success('Clocked on');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock on');
    } finally {
      setBusy(false);
    }
  };

  const openClockOff = () => {
    setTookBreak('no');
    setBreakMinutes('30');
    setBreakDialog(true);
  };

  const submitClockOff = async (overwrite = false) => {
    const { time } = localNow();
    const payload = {
      finish_time: time,
      break_minutes: tookBreak === 'yes' ? parseInt(breakMinutes, 10) : 0,
      overwrite,
    };
    setBusy(true);
    try {
      const res = await api.post('/api/mobile/timesheets/clock-off', payload);
      setShift(null);
      setBreakDialog(false);
      setOverwriteDialog(false);
      pendingOff.current = null;
      if (res.data?.timesheet_locked) {
        toast.warning(res.data.message || 'Clocked off. Timesheet was already submitted and not changed.');
      } else {
        // Show blocks preview if auto_blocks were generated
        const autoBlocks = res.data?.auto_blocks;
        if (autoBlocks && autoBlocks.length > 0) {
          setBlocksPreview(autoBlocks);
          toast.success(`Clocked off — ${autoBlocks.length} time block(s) generated from dockets`);
        } else {
          toast.success('Clocked off — timesheet updated');
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === 'entry_exists') {
        pendingOff.current = payload;
        setBreakDialog(false);
        setOverwriteDialog(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to clock off');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const clockedOn = !!shift;

  return (
    <>
      <Card
        className={clockedOn
          ? 'bg-gradient-to-r from-green-900/40 to-[#2a2a2a] border-green-600/50'
          : 'bg-[var(--m-bg-card)] border-[var(--m-border)]'}
        data-testid="clock-widget"
      >
        <CardContent className="p-4">
          {clockedOn ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-green-400 text-xs font-medium">
                  <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                  On the clock
                </div>
                <p className="text-[var(--m-text)] font-bold text-3xl tabular-nums mt-1" data-testid="clock-elapsed">{elapsed}</p>
                <p className="text-[var(--m-text-secondary)] text-xs mt-0.5">Started {shift.start_time}</p>
              </div>
              <Button
                onClick={openClockOff}
                disabled={busy}
                className="bg-red-600 hover:bg-red-700 text-[var(--m-text)] h-14 px-5 flex flex-col gap-0.5"
                data-testid="clock-off-button"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Square className="h-5 w-5" />}
                <span className="text-xs">Clock Off</span>
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-[#ffd108] text-xs font-medium">
                  <Clock className="h-3.5 w-3.5" />
                  Time tracking
                </div>
                <p className="text-[var(--m-text)] font-semibold text-sm mt-1">You're clocked off</p>
                <p className="text-[var(--m-text-secondary)] text-xs mt-0.5">Start your shift to track time</p>
              </div>
              <Button
                onClick={handleClockOn}
                disabled={busy}
                className="btn-volt h-14 px-5 flex flex-col gap-0.5"
                data-testid="clock-on-button"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
                <span className="text-xs">Clock On</span>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Break question on clock off */}
      <Dialog open={breakDialog} onOpenChange={setBreakDialog}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="clock-off-break-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)]">Did you take a break?</DialogTitle>
            <DialogDescription className="text-[var(--m-text-secondary)]">
              This is deducted from your total hours.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[var(--m-text-secondary)] text-xs">Took a break?</Label>
              <Select value={tookBreak} onValueChange={setTookBreak}>
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" data-testid="took-break-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No break</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {tookBreak === 'yes' && (
              <div>
                <Label className="text-[var(--m-text-secondary)] text-xs">Break length</Label>
                <Select value={breakMinutes} onValueChange={setBreakMinutes}>
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" data-testid="break-length-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBreakDialog(false)} className="text-[var(--m-text-secondary)]">Cancel</Button>
            <Button className="btn-volt" onClick={() => submitClockOff(false)} disabled={busy} data-testid="confirm-clock-off-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Clock Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overwrite confirmation */}
      <Dialog open={overwriteDialog} onOpenChange={setOverwriteDialog}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="clock-off-overwrite-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)] flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Entry already exists
            </DialogTitle>
            <DialogDescription className="text-[var(--m-text-secondary)]">
              You already have a timesheet entry for today. Do you want to overwrite it with your clocked times?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverwriteDialog(false)} className="text-[var(--m-text-secondary)]">Keep existing</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-[var(--m-text)]" onClick={() => submitClockOff(true)} disabled={busy} data-testid="confirm-overwrite-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Overwrite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Blocks preview after clock off */}
      <Dialog open={!!blocksPreview} onOpenChange={() => setBlocksPreview(null)}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-h-[70vh] overflow-y-auto" data-testid="blocks-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)] flex items-center gap-2">
              <Layers className="h-5 w-5 text-[#ffd108]" />
              Time Blocks Generated
            </DialogTitle>
            <DialogDescription className="text-[var(--m-text-secondary)]">
              {blocksPreview?.length} block(s) were auto-generated from your dockets. Review them on the Timesheet page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(blocksPreview || []).map((block, idx) => (
              <div
                key={block.block_id || idx}
                className={`rounded-md border p-2 text-sm ${
                  block.is_unallocated
                    ? 'border-dashed border-amber-500/40 bg-amber-900/10'
                    : 'border-[var(--m-border)] bg-[var(--m-bg)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {block.is_unallocated ? (
                      <Badge className="bg-amber-900/50 hover:bg-amber-900/70 text-amber-300 text-[10px]">Unallocated</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-[var(--m-border)]">
                        {block.job_number ? `#${block.job_number}` : 'Job'}
                      </Badge>
                    )}
                    {block.customer_name && (
                      <span className="text-xs text-[var(--m-text-secondary)]">{block.customer_name}</span>
                    )}
                  </div>
                  <span className="text-xs font-mono text-[var(--m-text)]">
                    {block.start_time}-{block.end_time} ({(block.hours || 0).toFixed(1)}h)
                  </span>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button className="btn-volt" onClick={() => setBlocksPreview(null)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MobileClockWidget;
